// Shared error-message extraction for react-admin HttpError shapes (A16 / NOTIF-21).
// Replaces the copy-pasted unwrap at data-export.js:543-578 — reordered to read
// errmsg before message (the precedent read message first; NOTIF-21 AC5 requires
// errmsg to win whenever both are present).
const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

const messageFromBody = (body) => {
  if (!body || typeof body !== "object") {
    return undefined;
  }
  if (typeof body.errmsg === "string" && body.errmsg.trim()) {
    return body.errmsg.trim();
  }
  if (typeof body.message === "string" && body.message.trim()) {
    return body.message.trim();
  }
  return undefined;
};

// NOTIF-21 AC6: the generic fallback carries the HTTP status alongside it so a
// 500/unparseable/no-message response is never shown as a bare, status-free string.
const withStatus = (message, error) => {
  if (!error?.status) {
    return message;
  }
  const statusText = error.statusText ? ` ${error.statusText}` : "";
  return `${message} (${error.status}${statusText})`;
};

export const extractApiErrorMessage = (error) => {
  if (error?.status >= 500) {
    // NOTIF-21 AC6 applies to every 5xx regardless of body shape — a structured
    // 500 with a body.message must still surface the generic + status fallback,
    // not the raw server message (only 400 honors errmsg/message, per AC5).
    return withStatus(GENERIC_ERROR_MESSAGE, error);
  }

  if (error?.body) {
    if (typeof error.body === "string") {
      try {
        const message = messageFromBody(JSON.parse(error.body));
        if (message) {
          return message;
        }
      } catch {
        // Unparseable body — fall through to the generic + status fallback below.
      }
    } else {
      const message = messageFromBody(error.body);
      if (message) {
        return message;
      }
    }
  } else if (error?.json) {
    const message = messageFromBody(error.json);
    if (message) {
      return message;
    }
  } else if (!error?.status && typeof error?.message === "string" && error.message.trim()) {
    // Real `HttpError`s (ra-core's fetchJson) always carry `.status`. When one
    // is present but body/json yielded no app-level message, `.message` is
    // just the transport layer's statusText echo, not a real server message
    // — NOTIF-21 AC6 treats that the same as "no message field": generic +
    // status, not the raw statusText surfaced as if it meant something.
    return error.message.trim();
  }

  return withStatus(GENERIC_ERROR_MESSAGE, error);
};
